import React, { useEffect, useState } from "react";

import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button, Form, Input,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";

import "./EditableFields.css";

import { BLOCKED_KEY_REGEX, ALLOWED_CHAR_REGEX } from "../../../../../../utils/common";

import { ReactComponent as CloseIcon } from "../../../../../../assets/images/onboarding/clearField.svg";

export const PRODUCT_RESERVED_FIELD_KEYS = ["sample_type", "received_date", "physical_category"];
export const ENVIRONMENT_RESERVED_FIELD_KEYS = [
  "sample_type", "received_date", "physical_category", "swab_number", "zone", "section",
];

export default function EditableFields({
  getJsonFieldName,
  onGetData = () => {},
  removableFieldIndex = undefined,
  fieldLimit = 6,
  disabled,
  reservedFieldKeys = [],

}) {
  const form = useFormInstance();
  const fieldsList = useWatch(["editable_fields"]);
  const [validating, setValidating] = useState(false);

  const handleValidateOnListChange = async () => {
    setValidating(true);
    form.validateFields(["editable_fields"], { recursive: true }).then(() => { // run validation on all items in array (need to recheck for duplicates when a field is removed)
      setValidating(false);
    })
      .catch(() => {
      /*
      errorInfo:
        {
          values: {
            username: 'username',
            password: 'password',
          },
          errorFields: [
            { name: ['password'], errors: ['Please input your Password!'] },
          ],
          outOfDate: false,
        }
      */
        setValidating(false);
      });
  };

  /** Validate on change, needed for duplicate checking */
  useEffect(() => {
    if (form && fieldsList) {
      handleValidateOnListChange();
    }
    // eslint-disable-next-line no-unused-expressions
    onGetData?.(fieldsList);
  }, [form, fieldsList]); // eslint-disable-line react-hooks/exhaustive-deps

  /** on key down in input, check if key is an invalid char, if yes, don't let user input key
   * call onKeyDown from props
   * @param {Event} e KeyDown event
   */
  const onKeyDown = (e) => {
  // Allow navigation/editing keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // Block all special characters except space, underscore and hyphen
    if (BLOCKED_KEY_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Form.List
      name="editable_fields"
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          {fields.map((field, index) => (
            <Form.Item
              required={false}
              key={field.key}
              className="EditableFields__DynamicItem"
            >
              <Form.Item
                {...field} // eslint-disable-line react/jsx-props-no-spreading
                rules={[
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      // console.log("duplicate validator", rule, value);
                      if (value) {
                        const _fields = getFieldValue("editable_fields");
                        const currJsonField = getJsonFieldName(value);
                        /** Check if duplicate of a fixed (non-editable) field for this form type */
                        if (reservedFieldKeys.includes(currJsonField)) {
                          return Promise.reject(new Error("Duplicate field"));
                        }

                        /** Check for invalid characters */
                        if (!ALLOWED_CHAR_REGEX.test(value)) {
                          return Promise.reject(
                            new Error("Only alphabets, numbers, spaces, hyphens (-), and underscores (_) are allowed"),
                          );
                        }

                        /** Check if duplicate of earlier (editable) field */
                        for (let i = 0; i < index; i++) {
                          const _field = _fields[i];
                          if (_field) {
                            const json_field = getJsonFieldName(_field);
                            if (currJsonField === json_field) {
                              return Promise.reject(new Error("Duplicate field"));
                            }
                          }
                        }
                        return Promise.resolve();
                      }

                      /** Return empty error only if validating is true */
                      if (validating) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(`Please input field name${index > 2 ? " or remove this field." : ""}`));
                    },
                    validateTrigger: "onBlur",
                  }),
                ]}
                noStyle
              >
                <Input
                  placeholder="Field name"
                  allowClear={{ clearIcon: <CloseIcon className="Clear__Button" /> }}
                  onKeyDown={onKeyDown}
                  disabled={disabled}
                />
              </Form.Item>
              {removableFieldIndex || removableFieldIndex === 0 ? (
                (index > removableFieldIndex) && (
                  <MinusCircleOutlined
                    className="EditableFields__DeleteIcon"
                    onClick={() => remove(field.name)}
                  />
                )
              ) : (
                <MinusCircleOutlined
                  className="EditableFields__DeleteIcon"
                  onClick={() => remove(field.name)}
                />
              )}
            </Form.Item>
          ))}
          {fields.length < fieldLimit && (
          <Form.Item>
            <Button
              type="primary"
              onClick={() => add()}
              icon={<PlusOutlined />}
              ghost
            >
              Add field
            </Button>
            <Form.ErrorList errors={errors} />
          </Form.Item>
          )}
        </>
      )}
    </Form.List>
  );
}
